import {
  ConfirmModal,
  CreateButton,
  Drawer,
  Flex,
  Table,
  TableRowMenuCell,
  Text,
  ToggleAllButton,
} from '@/components';
import { getOrderByTerm, sortEdges } from '@/features/project/_utils/activity';
import {
  IndicatorTypeLabelModifier,
  TermTypeLabelModifier,
} from '@/features/project/projects/_models/activity';
import {
  useActivities,
  useActivityTree,
  useCreateActivityTreeNode,
  useDeleteActivityTreeEdge,
  useUpdateActivityTreeEdge,
} from '@/features/project/projects/detail/activities/_hooks/activity';
import { useExpandedRowKeys, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { groupBy } from 'lodash-es';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import {
  ActivityTreeNodeForm,
  type ActivityTreeNodeFormValues,
} from './ActivityTreeNodeForm';

type DeletingEdge = {
  projectId: string;
  id: string;
  label: string | undefined;
};

const TABLE_CODE_WIDTH = 64;

const TypeLabelModifier = {
  1: IndicatorTypeLabelModifier.output,
  2: `${TermTypeLabelModifier.short}${IndicatorTypeLabelModifier.outcome}`,
  3: `${TermTypeLabelModifier.medium}${IndicatorTypeLabelModifier.outcome}`,
  4: `${TermTypeLabelModifier.long}${IndicatorTypeLabelModifier.outcome}`,
} as const;

export const ActivityFlowEdgesDrawer: FC = () => {
  const { t } = useTranslation();

  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const {
    expandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();

  const activities = useActivities({ projectId });

  const { mutateAsync: deleteActivityTreeEdgeAsync } =
    useDeleteActivityTreeEdge();
  const { mutateAsync: createActivityTreeNodeAsync } =
    useCreateActivityTreeNode();
  const { mutateAsync: updateActivityTreeEdgeAsync } =
    useUpdateActivityTreeEdge();

  const [deletingEdge, setDeletingEdge] = useState<DeletingEdge | null>(null);

  const activityTree = useActivityTree(projectId, (data) => {
    const activityTreeEdges = data.edges.map((edge) => {
      const targetNode = data.nodes.find(
        (node) => node.id === edge.targetNodeId,
      );
      const sourceNode = data.nodes.find(
        (node) => node.id === edge.sourceNodeId,
      );

      invariant(targetNode && sourceNode, t('unreachable-code'));

      return {
        ...edge,
        targetNode,
        sourceNode,
      };
    });

    const activityTreesGroupBySourceNodeId = Object.entries(
      groupBy(activityTreeEdges, (edge) => edge.sourceNode.id),
    ).map(([sourceNodeId, edges]) => {
      const sortedEdges = sortEdges(edges, 'targetNode');

      return {
        id: sourceNodeId,
        parentNode: sortedEdges.at(0)?.sourceNode,
        childrenEdges: sortedEdges,
      };
    });

    const sortedActivityTree = sortEdges(
      activityTreesGroupBySourceNodeId,
      'parentNode',
    );

    return sortedActivityTree;
  });

  const isOpenCollapses =
    drawerState.open && drawerState.type === 'activity-tree-node';
  const isOpenForm =
    drawerState.open && drawerState.type === 'activity-tree-node-form';
  const isEditForm = isOpenForm && drawerState.isEditing;

  const handleOpenActivityTreeNodeList = async () => {
    setDrawerState({ open: true, type: 'activity-tree-node' });
  };

  const handleCloseDrawer = () => {
    setDrawerState({ open: false });
  };

  const handleCreateActivityTreeNode = async (
    values: ActivityTreeNodeFormValues,
    shouldCloseDrawer?: boolean,
  ) => {
    if (
      values.nodeType === 'indicator' &&
      values.sourceIndicatorId === values.targetIndicatorId
    ) {
      await openMessage(
        'error',
        t('error-message-indicator-cannot-be-the-same'),
      );
      return;
    }

    await createActivityTreeNodeAsync({
      projectId,
      reasonWhyOutcomesCannotBeSetAtMultipleLevels:
        values.reasonWhyOutcomesCannotBeSetAtMultipleLevels ?? null,
      label: values.label ?? null,
      sourceObject: {
        activityId:
          values.nodeType === 'activity'
            ? (values.sourceActivityId ?? null)
            : null,
        indicatorId:
          values.nodeType === 'indicator'
            ? (values.sourceIndicatorId ?? null)
            : null,
      },
      targetObject: {
        activityId: null,
        indicatorId: values.targetIndicatorId,
      },
    });

    await Promise.all([
      openMessage('success', t('add-completed')),
      shouldCloseDrawer ? handleOpenActivityTreeNodeList() : undefined,
    ]);
  };

  const handleUpdateActivityTreeNode = async (
    values: ActivityTreeNodeFormValues,
  ) => {
    invariant(isEditForm, t('unreachable-code'));

    if (
      values.nodeType === 'indicator' &&
      values.sourceIndicatorId === values.targetIndicatorId
    ) {
      await openMessage(
        'error',
        t('error-message-indicator-cannot-be-the-same'),
      );
      return;
    }

    await updateActivityTreeEdgeAsync({
      projectId,
      id: drawerState.edgeId,
      label: values.label ?? null,
      reasonWhyOutcomesCannotBeSetAtMultipleLevels:
        values.reasonWhyOutcomesCannotBeSetAtMultipleLevels ?? null,
    });

    await Promise.all([
      openMessage('success', t('message-update-completed')),
      handleOpenActivityTreeNodeList(),
    ]);
  };

  const handleDeleteActivityTreeNode = async () => {
    invariant(deletingEdge, t('unreachable-code'));
    try {
      await deleteActivityTreeEdgeAsync({
        id: deletingEdge.id,
        projectId: deletingEdge.projectId,
      });

      await Promise.all([
        openMessage('success', t('message-delete-completed')),
        (async () => {
          setDeletingEdge(null);
        })(),
      ]);
    } catch {
      await openMessage('error', t('message-delete-failed'));
    }
  };

  const openAllExpanded = useCallback(() => {
    setExpandedRowKeys(activityTree.data?.map((node) => node.id) ?? []);
  }, [activityTree.data, setExpandedRowKeys]);

  useEffect(() => {
    openAllExpanded();
  }, [openAllExpanded]);

  return (
    <Drawer
      size={isOpenCollapses ? 'large' : 'default'}
      closeIcon={isOpenForm ? <ArrowLeftOutlined /> : undefined}
      open={isOpenCollapses || isOpenForm}
      onClose={async () => {
        isOpenForm
          ? await handleOpenActivityTreeNodeList()
          : handleCloseDrawer();
      }}
      destroyOnClose
      extra={
        isOpenCollapses && (
          <CreateButton
            onCreate={() => {
              setDrawerState({
                open: true,
                type: 'activity-tree-node-form',
                isEditing: false,
              });
            }}
          />
        )
      }
    >
      {isOpenCollapses && (
        <Flex vertical gap="small">
          <ToggleAllButton
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
          <Table
            enableSticky={false}
            rowKey="id"
            dataSource={activityTree.data}
            columns={[
              {
                title: t('sort-order'),
                key: 'parentNodeCode',
                width: TABLE_CODE_WIDTH,
                render: (_, record) => (
                  <Text
                    weight="regular"
                    text={
                      record.parentNode?.activity
                        ? record.parentNode.activity.sortOrder
                        : record.parentNode?.indicator?.sortOrder
                    }
                  />
                ),
              },
              {
                title: t('activity-type-title'),
                key: 'parentNodeType',
                render: (_, record) => (
                  <Text
                    weight="regular"
                    text={
                      record.parentNode?.activity
                        ? t('activity')
                        : TypeLabelModifier[
                            getOrderByTerm(record.parentNode?.indicator?.term)
                          ]
                    }
                  />
                ),
              },
            ]}
            pagination={false}
            expandable={{
              expandedRowKeys,
              onExpand: (expanded, record) => {
                onExpand(expanded, record.id);
              },
              expandedRowRender: (childrenNode) => (
                <Table
                  enableSticky={false}
                  rowKey="id"
                  style={{ marginLeft: TABLE_CODE_WIDTH }}
                  dataSource={childrenNode.childrenEdges}
                  pagination={false}
                  columns={[
                    {
                      title: t('sort-order'),
                      key: 'targetNodeCode',
                      width: TABLE_CODE_WIDTH,
                      render: (_, record) => (
                        <Text
                          weight="regular"
                          text={record.targetNode.indicator?.sortOrder}
                        />
                      ),
                    },
                    {
                      title: t('activity-type-title'),
                      key: 'targetNodeType',
                      width: 120,
                      render: (_, record) => {
                        return (
                          <Text
                            weight="regular"
                            text={
                              record.targetNode.indicator?.term
                                ? TypeLabelModifier[
                                    getOrderByTerm(
                                      record.targetNode.indicator.term,
                                    )
                                  ]
                                : t('output')
                            }
                          />
                        );
                      },
                    },
                    {
                      title: t('connection-to-next-outcome'),
                      key: 'label',
                      render: (_, record) => (
                        <Text weight="regular" text={record.label} />
                      ),
                    },
                    {
                      title: '',
                      key: 'actions',
                      fixed: 'right',
                      width: 48,
                      render: (edge) => (
                        <TableRowMenuCell
                          hideEditIcon
                          onEdit={() => {
                            setDrawerState({
                              open: true,
                              isEditing: true,
                              type: 'activity-tree-node-form',
                              edgeId: edge.id,
                              formValues: {
                                nodeType: edge.sourceNode.activity
                                  ? 'activity'
                                  : 'indicator',
                                label: edge.label ?? '',
                                reasonWhyOutcomesCannotBeSetAtMultipleLevels:
                                  edge.reasonWhyOutcomesCannotBeSetAtMultipleLevels ??
                                  '',
                                targetIndicatorId:
                                  edge.targetNode.indicatorId ?? '',
                                sourceActivityId:
                                  edge.sourceNode.activityId ?? undefined,
                                sourceIndicatorId:
                                  edge.sourceNode.indicatorId ?? undefined,
                              },
                            });
                          }}
                          items={[
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () => {
                                setDeletingEdge({
                                  projectId: edge.projectId,
                                  id: edge.id,
                                  label: edge.label ?? undefined,
                                });
                              },
                            },
                          ]}
                        />
                      ),
                    },
                  ]}
                />
              ),
            }}
          />
        </Flex>
      )}

      {isOpenForm && (
        <ActivityTreeNodeForm
          projectId={projectId}
          activityOptions={
            activities.data?.map((activity) => ({
              label: `${activity.sortOrder}: ${activity.activityContent}`,
              value: activity.id,
            })) ?? []
          }
          initialValues={isEditForm ? drawerState.formValues : undefined}
          isEdit={isEditForm}
          onSubmit={async ({ values, nextDrawerKey }) => {
            drawerState.isEditing
              ? await handleUpdateActivityTreeNode(values)
              : await handleCreateActivityTreeNode(values, !nextDrawerKey);
          }}
        />
      )}

      <ConfirmModal
        open={!!deletingEdge}
        skipCheck
        title={t('delete')}
        disclaimerText={
          deletingEdge?.label
            ? t('detach-edge-with-label', {
                label: deletingEdge.label,
              })
            : t('detach-edge')
        }
        onCancel={() => {
          setDeletingEdge(null);
        }}
        onOk={handleDeleteActivityTreeNode}
      />
    </Drawer>
  );
};
